import React, { useState, useEffect } from 'react';
import '../../styles/tableStyles.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';

const FullScreenTable = ({
  open,
  onClose,
  tableType,
  data = [],
  allColumns = [],
  defaultColumns = [],
  totalCount = 0,
  rowLimit = 1000,
  onDownload,
  onReset,
  bannerText = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Initialize visible columns when data changes
  useEffect(() => {
    if (allColumns.length > 0 && visibleColumns.length === 0) {
      setVisibleColumns(defaultColumns.filter(col => allColumns.includes(col)));
    }
  }, [allColumns, defaultColumns, visibleColumns.length]);

  // Update visible columns while preserving order
  const updateVisibleColumns = (newColumns) => {
    // Filter newColumns to only include columns that exist in allColumns
    const validColumns = newColumns.filter(col => allColumns.includes(col));
    
    // Maintain the order from allColumns
    const orderedColumns = allColumns.filter(col => validColumns.includes(col));
    
    setVisibleColumns(orderedColumns);
  };

  // Format column name for display
  const formatColumnName = (columnName) => {
    return columnName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter data based on search term
  const filterData = (data, searchTerm) => {
    if (!searchTerm.trim()) return data;
    
    const term = searchTerm.toLowerCase();
    return data.filter(row => 
      Object.values(row).some(value => 
        value && value.toString().toLowerCase().includes(term)
      )
    );
  };

  // Sort data
  const sortData = (data, field, direction) => {
    if (!field) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });
  };

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle reset
  const handleReset = () => {
    setSearchTerm('');
    updateVisibleColumns(defaultColumns.filter(col => allColumns.includes(col)));
    setSortField('');
    setSortDirection('asc');
    setPage(0);
    setRowsPerPage(25);
    if (onReset) onReset();
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get processed data
  const filteredData = filterData(data, searchTerm);
  const sortedData = sortData(filteredData, sortField, sortDirection);
  
  // Paginate sorted data
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Reset page when sort field changes
  useEffect(() => {
    setPage(0);
  }, [sortField, sortDirection, searchTerm]);

  // Download CSV
  const handleDownload = () => {
    if (onDownload) {
      onDownload(sortedData, visibleColumns);
    } else {
      // Default CSV download
      const csvContent = [
        // Header
        ['#', ...visibleColumns.map(formatColumnName)].join(','),
        // Data rows
        ...sortedData.map((row, index) => [
          index + 1,
          ...visibleColumns.map(col => {
            const value = row[col];
            // Escape CSV values
            if (value === null || value === undefined) return '';
            const stringValue = value.toString();
            return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
              ? `"${stringValue.replace(/"/g, '""')}"`
              : stringValue;
          })
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tableType}_data.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '95vw',
          maxWidth: 'none'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {formatColumnName(tableType)} Raw Data
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Download CSV" arrow>
              <IconButton size="small" color="primary" onClick={handleDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset Table" arrow>
              <IconButton size="small" color="primary" onClick={handleReset}>
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close" arrow>
              <IconButton size="small" color="primary" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {bannerText && (
          <Alert severity="info" sx={{ m: 2, mb: 0, flexShrink: 0 }}>
            {bannerText}
          </Alert>
        )}
        {/* Table Controls */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <TextField
              placeholder="Search within table..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Show/Hide Columns</InputLabel>
              <Select
                multiple
                value={visibleColumns}
                onChange={(e) => updateVisibleColumns(e.target.value)}
                renderValue={(selected) => `${selected.length} columns`}
                label="Show/Hide Columns"
              >
                {allColumns.map(column => (
                  <MenuItem key={column} value={column}>
                    {formatColumnName(column)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {/* Row count info */}
          <Typography variant="body2" color="text.secondary">
            Displayed rows: {sortedData.length.toLocaleString()} of {totalCount.toLocaleString()}
            {searchTerm && ` (filtered by "${searchTerm}")`}
          </Typography>
        </Box>
        
        {/* Table Display */}
        <TableContainer sx={{ 
          flex: 1,
          overflow: 'auto',
          minHeight: 0
        }}>
          <Table stickyHeader size="small" className="search-table">
            <TableHead>
              <TableRow>
                <TableCell className="row-number-cell">#</TableCell>
                {visibleColumns.map(column => (
                  <TableCell 
                    key={column}
                    className="table-header-cell"
                    onClick={() => handleSort(column)}
                    sx={{ 
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                  >
                    {formatColumnName(column)}
                    {getSortIcon(column) && (
                      <span style={{ marginLeft: '4px' }}>
                        {getSortIcon(column)}
                      </span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow key={index} className="table-row">
                  <TableCell className="row-number-cell">{page * rowsPerPage + index + 1}</TableCell>
                  {visibleColumns.map(column => {
                    // Check if this is a plugging cost column and should show "SEE OTHER API RECORD"
                    const isPluggingCostColumn = column.toLowerCase().includes('plugging_cost') || 
                                                  column.toLowerCase() === 'plugging cost';
                    const pluggingCostValue = row[column];
                    const apiRecordCount = row['API_RECORD_COUNT'] || row['api_record_count'] || row['API Record Count'] || 0;
                    
                    // If plugging cost is null/undefined/empty and API_RECORD_COUNT > 1, show note
                    let displayValue = row[column];
                    if (isPluggingCostColumn && 
                        (pluggingCostValue === null || pluggingCostValue === undefined || pluggingCostValue === '') &&
                        apiRecordCount > 1) {
                      displayValue = 'SEE OTHER API RECORD';
                    }
                    
                    return (
                    <TableCell key={column} className="table-body-cell">
                        {displayValue !== null && displayValue !== undefined ? displayValue.toString() : ''}
                    </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ flexShrink: 0 }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
            sx={{
              '& .MuiTablePagination-toolbar': {
                justifyContent: 'center',
                minHeight: '52px', // Prevent compression
              },
              '& .MuiTablePagination-spacer': {
                display: 'none',
              },
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FullScreenTable;
